import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { TextField, TextFieldProps } from '@mui/material';

/**
 * A controlled TextField that allows decimal input without stripping
 * intermediate characters (e.g. "10." or "10.50") while the user is typing.
 *
 * It maintains a local string state during focus and syncs the numeric value
 * to the parent via `onNumericChange`. Input is restricted to at most
 * `maxDecimalPlaces` digits after the decimal point (default: 2).
 *
 * Scenarios handled:
 * ─────────────────
 * 1.  Typing a decimal point    → "10." is preserved while typing (not reset to "10")
 * 2.  Trailing zeros             → "10.50" stays as-is during focus, cleaned on blur to "10.5"
 * 3.  Leading decimal            → ".5" is accepted, parsed as 0.5
 * 4.  Max decimal places         → Only up to `maxDecimalPlaces` digits after "." (default 2)
 *                                   e.g. "10.52" allowed, "10.523" auto-truncated to "10.52"
 * 5.  Empty / cleared field      → Treated as 0 during typing; resets to "0" on blur
 * 6.  Just a dot "."             → Treated as 0 during typing; resets to "0" on blur
 * 7.  Negative values            → Blocked by default; set `allowNegative={true}` to permit
 * 8.  Non-numeric characters     → Letters, symbols, etc. are silently rejected
 * 9.  Multiple dots              → "10.5.3" is rejected by regex
 * 10. Paste with excess decimals → Auto-truncated to `maxDecimalPlaces` (e.g. "10.555" → "10.55")
 * 11. Paste invalid content      → Non-numeric paste content is silently rejected
 * 12. External value change      → When parent updates `numericValue` while field is NOT focused,
 *                                   the display syncs automatically
 * 13. Focus select-all           → On focus, entire text is selected for easy replacement
 * 14. Blur cleanup               → On blur, raw string is normalized (e.g. "10." → "10", "" → "0")
 * 15. Integer-only input         → Set `maxDecimalPlaces={0}` to disallow any decimal point
 * 16. Leading zeros              → "007" normalized to "7" on blur; "00.5" → "0.5"
 * 17. NaN / Infinity prop        → Safeguarded — treated as 0 if `numericValue` is invalid
 * 18. Min / Max clamping         → Optional `min` and `max` props; clamped on blur
 * 19. Large number overflow      → Guarded by configurable `max` (defaults to 9999999999999.99)
 *
 * @example
 * // Currency field (no negatives, 2 decimals, 0–∞)
 * <DecimalTextField
 *   size="small"
 *   numericValue={amount}
 *   onNumericChange={(val) => setAmount(val)}
 *   InputProps={{
 *     startAdornment: <InputAdornment position="start">$</InputAdornment>,
 *   }}
 * />
 *
 * @example
 * // Percent field (0–100, 2 decimals)
 * <DecimalTextField
 *   size="small"
 *   numericValue={percent}
 *   onNumericChange={(val) => setPercent(val)}
 *   min={0}
 *   max={100}
 *   InputProps={{
 *     endAdornment: <InputAdornment position="end">%</InputAdornment>,
 *   }}
 * />
 */

/** Safe default ceiling to prevent overflow. */
const DEFAULT_MAX = 9999999999999.99;

/** Sanitize an external numeric prop (guards NaN / Infinity / undefined). */
const safeNumber = (v: unknown): number => {
  if (typeof v !== 'number' || !Number.isFinite(v)) return 0;
  return v;
};

/**
 * Truncate (not round) a numeric string to at most `decimals` places
 * after the decimal point so pasted values are gracefully shortened.
 *
 * "10.5678" → "10.56" (when decimals = 2)
 * "10"      → "10"    (no decimal point, no change)
 */
const truncateDecimals = (raw: string, decimals: number): string => {
  const dotIdx = raw.indexOf('.');
  if (dotIdx === -1) return raw; // no dot
  // If there is more than one dot, preserve the raw string so that
  // downstream validation (regex) can correctly reject inputs like "10.5.3".
  const secondDotIdx = raw.indexOf('.', dotIdx + 1);
  if (secondDotIdx !== -1) return raw;
  if (decimals === 0) return raw.slice(0, dotIdx); // integer-only mode
  return raw.slice(0, dotIdx + 1 + decimals);
};

/**
 * Strip unnecessary leading zeros: "007" → "7", "00.5" → "0.5", "0" → "0".
 */
const stripLeadingZeros = (raw: string): string => {
  // Preserve a single leading minus
  const isNeg = raw.startsWith('-');
  let s = isNeg ? raw.slice(1) : raw;

  // Remove leading zeros but keep at least one digit before a dot or alone
  s = s.replace(/^0+(\d)/, '$1'); // "007" → "7", "00.5" stays "0.5" via next line
  if (s.startsWith('.')) s = '0' + s; // ".5" → "0.5"

  return (isNeg ? '-' : '') + s;
};

export interface DecimalTextFieldProps
  extends Omit<TextFieldProps, 'value' | 'onChange' | 'onFocus' | 'onBlur'> {
  /** The current numeric value (controlled). */
  numericValue: number;
  /** Called whenever the parsed numeric value changes. */
  onNumericChange: (value: number) => void;
  /** Maximum digits allowed after the decimal point. Defaults to 2. */
  maxDecimalPlaces?: number;
  /** Allow negative input. Defaults to false (positive-only). */
  allowNegative?: boolean;
  /** Minimum allowed value (clamped on blur). Defaults to 0 when allowNegative is false. */
  min?: number;
  /** Maximum allowed value (clamped on blur). Defaults to 9 999 999 999 999.99. */
  max?: number;
}

const DecimalTextField: React.FC<DecimalTextFieldProps> = ({
  numericValue: rawNumericValue,
  onNumericChange,
  maxDecimalPlaces = 2,
  allowNegative = false,
  min: minProp,
  max: maxProp,
  ...textFieldProps
}) => {
  // ── Derived constants ──────────────────────────────────────────────
  const numericValue = safeNumber(rawNumericValue);
  const minVal = minProp ?? (allowNegative ? -DEFAULT_MAX : 0);
  const maxVal = maxProp ?? DEFAULT_MAX;

  // Memoize regex so it's stable across renders (used inside useCallback)
  const pattern = useMemo(() => {
    const sign = allowNegative ? '-?' : '';
    return new RegExp(`^${sign}\\d*\\.?\\d{0,${maxDecimalPlaces}}$`);
  }, [maxDecimalPlaces, allowNegative]);

  // ── Local state ────────────────────────────────────────────────────
  const [rawValue, setRawValue] = useState<string>(String(numericValue));
  const [isFocused, setIsFocused] = useState(false);

  // Sync local state from external numeric value when not focused
  useEffect(() => {
    if (!isFocused) {
      setRawValue(String(numericValue));
    }
  }, [numericValue, isFocused]);

  // ── Handlers ───────────────────────────────────────────────────────

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let raw = e.target.value;

      // Strip negative sign if not allowed
      if (!allowNegative) {
        raw = raw.replace(/-/g, '');
      }

      // Auto-truncate excess decimal places (handles paste of e.g. "10.555")
      raw = truncateDecimals(raw, maxDecimalPlaces);

      // Reject if still doesn't match the allowed pattern
      if (raw !== '' && !pattern.test(raw)) return;

      setRawValue(raw);

      const parsed = parseFloat(raw);
      if (!isNaN(parsed)) {
        // Normalize -0 to 0
        onNumericChange(Object.is(parsed, -0) ? 0 : parsed);
      } else {
        // Covers: "", ".", "-", "-." — all treated as 0
        onNumericChange(0);
      }
    },
    [onNumericChange, maxDecimalPlaces, allowNegative, pattern],
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      setRawValue(String(numericValue));
      // Use setTimeout so the select() fires after React flushes the new value
      setTimeout(() => e.target.select(), 0);
    },
    [numericValue],
  );

  const handleBlur = useCallback(() => {
    setIsFocused(false);

    let parsed = parseFloat(rawValue);

    // Invalid / empty → 0
    if (isNaN(parsed) || rawValue === '') {
      parsed = 0;
    }

    // Clamp to [min, max]
    if (parsed < minVal) parsed = minVal;
    if (parsed > maxVal) parsed = maxVal;

    // Normalize display (strip leading zeros, trailing dot, etc.)
    const display = stripLeadingZeros(String(parsed));
    setRawValue(display);
    onNumericChange(parsed);
  }, [rawValue, onNumericChange, minVal, maxVal]);

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <TextField
      {...textFieldProps}
      type="text"
      inputMode="decimal"
      value={isFocused ? rawValue : String(numericValue)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};

export default DecimalTextField;
